import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  CircularProgress,
  Button
} from '@mui/material';
import { Close, AttachFile } from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import StoreDocumentsDialog from './StoreDocumentsDialog';

const whoName = (who) => (who ? `${who.firstName || ''} ${who.lastName || ''}`.trim() || '—' : '—');

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

const calibrationStatusColor = (status) => {
  if (status === 'CALIBRATED') return 'success';
  if (status === 'OVERDUE') return 'error';
  if (status === 'DUE') return 'warning';
  return 'default';
};

const ToolDetailModal = ({ toolId, open, onClose, onToolUpdated }) => {
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [documentsOpen, setDocumentsOpen] = useState(false);
  const { showToast } = useToast();

  const fetchTool = useCallback(async () => {
    if (!toolId) return;
    setLoading(true);
    try {
      const res = await axios.get(`/api/store/tools/${toolId}`);
      setTool(res.data);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || 'Failed to load tool', 'error');
    } finally {
      setLoading(false);
    }
  }, [toolId, showToast]);

  useEffect(() => {
    if (open && toolId) {
      fetchTool();
    }
  }, [open, toolId, fetchTool]);

  useEffect(() => {
    if (!open) {
      setTabValue(0);
      setTool(null);
    }
  }, [open]);

  const handleClose = () => {
    setTool(null);
    onClose();
  };

  const calibrationRows = useMemo(() => {
    if (!tool?.calibrationHistory?.length) return [];
    const sorted = [...tool.calibrationHistory].sort(
      (a, b) => new Date(b.calibratedDate) - new Date(a.calibratedDate)
    );
    const chronological = [...tool.calibrationHistory].sort(
      (a, b) => new Date(a.calibratedDate) - new Date(b.calibratedDate)
    );
    const intervalByDate = new Map();
    chronological.forEach((entry, index) => {
      const previous = index > 0 ? chronological[index - 1] : null;
      let intervalDays = null;
      if (previous?.calibratedDate && entry.calibratedDate) {
        intervalDays = Math.round(
          (new Date(entry.calibratedDate) - new Date(previous.calibratedDate)) / (1000 * 60 * 60 * 24)
        );
      }
      intervalByDate.set(new Date(entry.calibratedDate).toISOString(), intervalDays);
    });
    return sorted.map((entry) => ({
      ...entry,
      intervalDays: intervalByDate.get(new Date(entry.calibratedDate).toISOString()) ?? null
    }));
  }, [tool]);

  const statusRows = useMemo(() => {
    if (!tool?.statusHistory?.length) return [];
    return [...tool.statusHistory].sort(
      (a, b) => new Date(b.changedAt) - new Date(a.changedAt)
    );
  }, [tool]);

  const locationString = (location) => {
    if (!location) return '—';
    const parts = [location.warehouse, location.shelf, location.bin].filter(Boolean);
    return parts.length ? parts.join(' / ') : '—';
  };

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {tool ? `${tool.toolNumber} — ${tool.description}` : 'Tool details'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loading && !tool ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : tool ? (
            <>
              <Tabs value={tabValue} onChange={(_, value) => setTabValue(value)} sx={{ mb: 2 }}>
                <Tab label="Overview" />
                <Tab label="Calibration History" />
                <Tab label="Status History" />
                <Tab label="Documents" />
              </Tabs>

              {tabValue === 0 && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Tool number</Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                      {tool.toolNumber}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Category</Typography>
                    <Typography variant="body1">
                      <Chip label={tool.category} size="small" />
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography variant="body1">{tool.description || '—'}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Calibration status</Typography>
                    <Typography variant="body1">
                      <Chip
                        label={tool.calibrationStatus}
                        size="small"
                        color={calibrationStatusColor(tool.calibrationStatus)}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Serviceability</Typography>
                    <Typography variant="body1">
                      <Chip
                        label={tool.serviceabilityStatus}
                        size="small"
                        color={tool.serviceabilityStatus === 'SERVICEABLE' ? 'success' : 'error'}
                      />
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" color="text.secondary">Calibration due</Typography>
                    <Typography
                      variant="body1"
                      color={
                        tool.calibrationDueDate && new Date(tool.calibrationDueDate) < new Date()
                          ? 'error'
                          : 'text.primary'
                      }
                    >
                      {formatDate(tool.calibrationDueDate)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Assigned to</Typography>
                    <Typography variant="body1">
                      {tool.assignedTo ? whoName(tool.assignedTo) : 'Available'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="caption" color="text.secondary">Location</Typography>
                    <Typography variant="body1">{locationString(tool.location)}</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Calibration events recorded
                    </Typography>
                    <Typography variant="body1">{tool.calibrationHistory?.length || 0}</Typography>
                  </Grid>
                </Grid>
              )}

              {tabValue === 1 && (
                <Box>
                  {calibrationRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No calibration history recorded yet.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Performed by</TableCell>
                          <TableCell>Certificate #</TableCell>
                          <TableCell>Next due</TableCell>
                          <TableCell>Interval (days)</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {calibrationRows.map((entry, index) => (
                          <TableRow key={`${entry.calibratedDate}-${index}`}>
                            <TableCell>{formatDate(entry.calibratedDate)}</TableCell>
                            <TableCell>{whoName(entry.calibratedBy)}</TableCell>
                            <TableCell>{entry.certificateNumber || '—'}</TableCell>
                            <TableCell>{formatDate(entry.nextDueDate)}</TableCell>
                            <TableCell>{entry.intervalDays != null ? entry.intervalDays : '—'}</TableCell>
                            <TableCell>{entry.notes || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}

              {tabValue === 2 && (
                <Box>
                  {statusRows.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No status history recorded yet.
                    </Typography>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell>Changed by</TableCell>
                          <TableCell>When</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {statusRows.map((entry, index) => (
                          <TableRow key={`${entry.status}-${entry.changedAt}-${index}`}>
                            <TableCell>
                              <Chip label={entry.status} size="small" />
                            </TableCell>
                            <TableCell>{whoName(entry.changedBy)}</TableCell>
                            <TableCell>{formatDateTime(entry.changedAt)}</TableCell>
                            <TableCell>{entry.notes || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}

              {tabValue === 3 && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Attach calibration certificates and related tool documentation.
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AttachFile />}
                    onClick={() => setDocumentsOpen(true)}
                  >
                    Manage documents
                  </Button>
                </Box>
              )}
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Tool not found.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      <StoreDocumentsDialog
        open={documentsOpen}
        onClose={() => {
          setDocumentsOpen(false);
          fetchTool();
          onToolUpdated?.();
        }}
        entityId={toolId}
        entityType="tool"
        entityLabel={tool ? `${tool.toolNumber} - ${tool.description}` : ''}
      />
    </>
  );
};

export default ToolDetailModal;
